/**
 * Markdown mode of a section (spec §8.19): CodeMirror over the markdown serialisation of the section's
 * Loro document. Nothing is stored as markdown (Q36): edits are parsed and applied as the smallest
 * ProseMirror replace through a hidden view bound to the same container (spike S14); changes from
 * elsewhere re-serialise into the editor as a minimal text change.
 */
import { defaultKeymap } from "@codemirror/commands";
import { Annotation, Compartment, EditorState } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import type { ContainerID, LoroDoc } from "loro-crdt";
import { toMarkdown } from "./markdown";
import { applyMarkdown } from "./markdownApply";
import { mountProseMirror } from "./ProseMirrorView";
import { docFromLoro, touches } from "./RenderedView";

const FLUSH_MS = 300;
const fromDoc = Annotation.define<boolean>();

export interface MarkdownMount {
  view: EditorView;
  /** apply unflushed edits now (mode switch, blur) */
  flush(): void;
  destroy(): void;
}

export function mountMarkdown(parent: HTMLElement, opts: { doc: LoroDoc; containerId: ContainerID; readOnly: boolean; onExitUp?: () => void; onExitDown?: () => void; onFocus?: () => void }): MarkdownMount {
  // the hidden binding: no undo plugin (the page UndoManager stays with visible editors), no cursor
  const hiddenHost = document.createElement("div");
  const hidden = mountProseMirror(hiddenHost, { doc: opts.doc, containerId: opts.containerId, undoManager: null, presence: null, readOnly: false });
  let dirty = false;
  let writing = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const flush = () => {
    clearTimeout(timer);
    if (!dirty || opts.readOnly) return;
    // the hidden view loads the document asynchronously: diffing against a view that has not caught up
    // with Loro would rewrite the whole section, so wait for it
    if (!hidden.state.doc.eq(docFromLoro(opts.doc, opts.containerId))) {
      timer = setTimeout(flush, 30);
      return;
    }
    dirty = false;
    writing = true;
    try {
      applyMarkdown(hidden, view.state.doc.toString());
    } finally {
      writing = false;
    }
  };

  const ro = new Compartment();
  const lang = new Compartment();
  const view = new EditorView({
    parent,
    state: EditorState.create({
      doc: toMarkdown(docFromLoro(opts.doc, opts.containerId)),
      extensions: [
        keymap.of([
          { key: "ArrowUp", run: (v) => (opts.onExitUp && v.state.doc.lineAt(v.state.selection.main.head).number === 1 ? (flush(), opts.onExitUp(), true) : false) },
          { key: "ArrowDown", run: (v) => (opts.onExitDown && v.state.doc.lineAt(v.state.selection.main.head).number === v.state.doc.lines ? (flush(), opts.onExitDown(), true) : false) },
          // undo is the page's (Loro) undo: CodeMirror keeps no history of its own
          ...defaultKeymap.filter((b) => !["Mod-z", "Mod-y", "Mod-Shift-z"].includes(b.key ?? "")),
        ]),
        ro.of([EditorState.readOnly.of(opts.readOnly), EditorView.editable.of(!opts.readOnly)]),
        EditorView.lineWrapping,
        EditorView.updateListener.of((u) => {
          if (!u.docChanged || u.transactions.every((tr) => tr.annotation(fromDoc))) return;
          dirty = true;
          clearTimeout(timer);
          timer = setTimeout(flush, FLUSH_MS);
        }),
        EditorView.domEventHandlers({
          focus: () => {
            opts.onFocus?.();
            return false;
          },
          blur: () => {
            flush();
            return false;
          },
        }),
        EditorView.theme({ "&": { fontSize: "0.9rem" }, ".cm-content": { fontFamily: "ui-monospace, monospace" } }),
        lang.of([]),
      ],
    }),
  });
  view.dom.dataset.testid = "section-markdown";
  void import("@codemirror/lang-markdown").then(({ markdown }) => {
    if (view.dom.isConnected) view.dispatch({ effects: lang.reconfigure(markdown()) });
  });

  // changes from anywhere else (other readers, other views, undo) → the markdown text
  const hit = touches(opts.doc, opts.containerId);
  let queued = false;
  const unsub = opts.doc.subscribe((batch) => {
    if (writing || queued || !hit(batch.events)) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      // unflushed local text wins: it is applied first and the result read back after
      if (dirty) {
        flush();
        if (dirty) return;
      }
      const next = toMarkdown(docFromLoro(opts.doc, opts.containerId));
      const current = view.state.doc.toString();
      if (next === current) return;
      let start = 0;
      while (start < next.length && start < current.length && next[start] === current[start]) start++;
      let endC = current.length;
      let endN = next.length;
      while (endC > start && endN > start && current[endC - 1] === next[endN - 1]) {
        endC--;
        endN--;
      }
      view.dispatch({ changes: { from: start, to: endC, insert: next.slice(start, endN) }, annotations: fromDoc.of(true) });
    }, 0);
  });

  return {
    view,
    flush,
    destroy() {
      flush();
      clearTimeout(timer);
      unsub();
      view.destroy();
      hidden.destroy();
    },
  };
}
